/**
 * Shared validation utilities for the admin panel.
 * Centralizes phone, email, pincode, GST, and numeric validation
 * so every form uses the same rules.
 */

import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js/max';

// ──────────────────────────────────────────────
// Phone validation (International numbers)
// ──────────────────────────────────────────────

/**
 * Strips non-phone characters in real-time.
 * Keeps digits, +, space, hyphen, parentheses.
 * Enforces max length of 20 chars (covers international numbers with country code).
 */
export const sanitizePhoneInput = (value) => {
  const cleaned = value.replace(/[^\d\s\-+()]/g, '');
  return cleaned.slice(0, 20);
};

/**
 * Validates a phone number (international support).
 * Accepts numbers in formats like:
 *   +971XXXXXXXXXX (UAE/Dubai)
 *   +1XXXXXXXXXX (Canada/US)
 *   +91XXXXXXXXXX (India)
 *   XXXXXXXXXX (10-digit Indian)
 *   XXXXXXX (7+ digit international)
 *
 * Cleaning rules:
 *  - Strips spaces, hyphens, parentheses, dots
 *  - Preserves leading + for country code
 *  - If starts with +, keeps the full international number
 *  - If no +, treats as a local number (requires 7-15 digits)
 *
 * Returns { isValid, error?, cleanPhone? }.
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Strip spaces, hyphens, parentheses, dots, brackets
  let cleanPhone = phone.toString().replace(/[\s\-().[\]{}]/g, '');

  const hasPlus = cleanPhone.startsWith('+');

  // Remove all non-digit characters except leading +
  if (hasPlus) {
    // Keep the + and digits only
    cleanPhone = '+' + cleanPhone.substring(1).replace(/[^\d]/g, '');
  } else {
    // Remove common country code prefixes without + (e.g., 91 for India)
    cleanPhone = cleanPhone.replace(/[^\d]/g, '');
  }

  // If empty after cleaning
  if (!cleanPhone || cleanPhone === '+') {
    return { isValid: false, error: 'Phone number is required' };
  }

  if (hasPlus) {
    // International format: +<country_code><number>
    const digitsOnly = cleanPhone.substring(1);
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      return { isValid: false, error: 'Phone must be 7-15 digits (excluding +)' };
    }
  } else {
    // No country code provided — validate as local number (7-15 digits)
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      return { isValid: false, error: 'Phone must be 7-15 digits' };
    }
  }

  return { isValid: true, cleanPhone };
};

/**
 * Validates an optional phone number.
 * If empty, returns valid. If provided, validates with international support.
 */
export const validateOptionalPhone = (phone) => {
  if (!phone || !phone.trim()) {
    return { isValid: true, cleanPhone: '' };
  }
  return validatePhoneNumber(phone);
};

const countryName = (code) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

/**
 * Strict per-country validation (used by lead import). Checks the number
 * against the real numbering plan of its country — e.g. UAE mobiles must be
 * +971 5X XXX XXXX, US numbers +1 + 10 digits, UK +44 + 10 digits.
 *
 *  - "+<cc>…" or "00<cc>…"  → validated for that country, returned as E.164
 *  - no country code         → validated as a defaultCountry number (the
 *                              country picked on the form). India is kept as
 *                              the national number (9876543210) to match
 *                              existing data; other countries become E.164
 *                              ("0501234567" + UAE → "+971501234567")
 *  - Excel often drops "+"   → "971501234567" is retried as "+971501234567"
 *
 * Returns { isValid, error?, cleanPhone?, country? }.
 */
export const validateInternationalPhone = (phone, defaultCountry = 'IN') => {
  if (!isSupportedCountry(defaultCountry)) defaultCountry = 'IN';
  if (!phone || !phone.toString().trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  let raw = phone.toString().trim().replace(/[\s\-().[\]{}]/g, '');
  if (raw.startsWith('00')) raw = '+' + raw.slice(2);
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');

  if (!digits) return { isValid: false, error: 'Phone number is required' };
  if (digits.length < 7 || digits.length > 15) {
    return { isValid: false, error: 'Phone must be 7-15 digits' };
  }

  if (hasPlus) {
    const parsed = parsePhoneNumberFromString('+' + digits);
    if (!parsed || !parsed.countryCallingCode) {
      return { isValid: false, error: `Unknown country code in +${digits}` };
    }
    if (!parsed.country || !parsed.isValid()) {
      const where = parsed.country
        ? `${countryName(parsed.country)} (+${parsed.countryCallingCode})`
        : `country code +${parsed.countryCallingCode}`;
      return { isValid: false, error: `Invalid number for ${where}` };
    }
    return { isValid: true, cleanPhone: parsed.number, country: parsed.country };
  }

  const local = parsePhoneNumberFromString(digits, defaultCountry);
  if (local && local.isValid()) {
    const cleanPhone = defaultCountry === 'IN' ? local.nationalNumber : local.number;
    return { isValid: true, cleanPhone, country: local.country };
  }

  const intl = parsePhoneNumberFromString('+' + digits);
  if (intl && intl.isValid()) {
    return { isValid: true, cleanPhone: intl.number, country: intl.country };
  }

  return {
    isValid: false,
    error: `Invalid ${countryName(defaultCountry)} (+${getCountryCallingCode(defaultCountry)}) number — if it is from another country, include its country code`,
  };
};

// Country dropdown options for phone fields, e.g.
// { value: 'AE', label: 'United Arab Emirates (+971)' }. India and the Gulf
// countries are listed first, the rest alphabetically.
const PINNED_PHONE_COUNTRIES = ['IN', 'AE', 'SA', 'QA', 'OM', 'KW', 'BH', 'US', 'GB'];
// Extra search words for the searchable country dropdown
const COUNTRY_SEARCH_ALIASES = {
  AE: 'uae dubai abu dhabi sharjah emirates',
  GB: 'uk england britain',
  US: 'usa america',
  SA: 'ksa riyadh jeddah',
  IN: 'bharat',
};
const toCountryOption = (code) => ({
  value: code,
  label: `${countryName(code)} (+${getCountryCallingCode(code)})`,
  keywords: COUNTRY_SEARCH_ALIASES[code] || '',
});
export const PHONE_COUNTRY_OPTIONS = [
  ...PINNED_PHONE_COUNTRIES.map(toCountryOption),
  ...getCountries()
    .filter((c) => !PINNED_PHONE_COUNTRIES.includes(c))
    .map(toCountryOption)
    .sort((a, b) => a.label.localeCompare(b.label)),
];

// ──────────────────────────────────────────────
// Country suggestions for numbers that failed validation
// ──────────────────────────────────────────────

// Number types that make sense for a lead; premium-rate, toll-free, UAN etc.
// matches are noise.
const LEAD_NUMBER_TYPES = ['MOBILE', 'FIXED_LINE_OR_MOBILE', 'FIXED_LINE', undefined];
const suggestionCache = new Map();

/**
 * Detects which countries a number (written without a country code) would be
 * valid in, best match first. Ranking: countries seen elsewhere in the same
 * file (boost) → India/Gulf/US/UK → mobile numbers → the rest.
 *
 * Returns [{ country, label, formatted, type }] (max `limit`); empty for
 * numbers that already carry a "+" / "00" country code.
 */
export const suggestPhoneCountries = (phone, { boost = [], exclude = [], limit = 3 } = {}) => {
  const raw = String(phone || '').trim().replace(/[\s\-().[\]{}]/g, '');
  if (!raw || raw.startsWith('+') || raw.startsWith('00')) return [];
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 6 || digits.length > 15) return [];

  let matches = suggestionCache.get(digits);
  if (!matches) {
    matches = [];
    getCountries().forEach((code) => {
      const parsed = parsePhoneNumberFromString(digits, code);
      if (!parsed || !parsed.isValid()) return;
      const type = parsed.getType();
      if (!LEAD_NUMBER_TYPES.includes(type)) return;
      matches.push({
        country: code,
        label: toCountryOption(code).label,
        formatted: parsed.formatInternational(),
        type,
      });
    });
    suggestionCache.set(digits, matches);
  }

  const score = (m) => {
    let s = 0;
    const b = boost.indexOf(m.country);
    if (b !== -1) s += 1000 - b;
    const p = PINNED_PHONE_COUNTRIES.indexOf(m.country);
    if (p !== -1) s += 500 - p;
    if (m.type === 'MOBILE' || m.type === 'FIXED_LINE_OR_MOBILE') s += 100;
    return s;
  };

  return matches
    .filter((m) => !exclude.includes(m.country))
    .sort((a, b) => score(b) - score(a) || a.label.localeCompare(b.label))
    .slice(0, limit);
};

/** Country of an already-validated number ("+9715…" → "AE", national → defaultCountry). */
export const detectPhoneCountry = (phone, defaultCountry = 'IN') => {
  const parsed = parsePhoneNumberFromString(String(phone || ''), defaultCountry);
  return parsed && parsed.isValid() ? parsed.country : null;
};

// Last country picked on a phone form, remembered per browser.
const PHONE_COUNTRY_KEY = 'leadPhoneCountry';
export const getSavedPhoneCountry = () => {
  try {
    const saved = localStorage.getItem(PHONE_COUNTRY_KEY);
    return saved && isSupportedCountry(saved) ? saved : 'IN';
  } catch {
    return 'IN';
  }
};
export const savePhoneCountry = (code) => {
  try {
    localStorage.setItem(PHONE_COUNTRY_KEY, code);
  } catch {
    // storage unavailable — the choice just isn't remembered
  }
};

export const validateOptionalInternationalPhone = (phone, defaultCountry = 'IN') => {
  if (!phone || !phone.toString().trim()) {
    return { isValid: true, cleanPhone: '' };
  }
  return validateInternationalPhone(phone, defaultCountry);
};

// ──────────────────────────────────────────────
// Pincode / Zip code validation
// ──────────────────────────────────────────────

/**
 * Strips non-digit characters for pincode/zip input.
 * Enforces max length.
 */
export const sanitizePincodeInput = (value, maxLength = 6) => {
  const digits = value.replace(/\D/g, '');
  return digits.slice(0, maxLength);
};

/**
 * Validates an Indian pincode (6 digits).
 * Optional: if empty, returns valid.
 */
export const validatePincode = (pincode) => {
  if (!pincode || !pincode.trim()) {
    return { isValid: true, cleanPincode: '' };
  }
  const clean = pincode.trim();
  if (!/^\d{6}$/.test(clean)) {
    return { isValid: false, error: 'Pincode must be 6 digits' };
  }
  return { isValid: true, cleanPincode: clean };
};

/**
 * Validates a zip/postal code (up to 10 digits).
 * Optional: if empty, returns valid.
 */
export const validateZipCode = (zipCode) => {
  if (!zipCode || !zipCode.trim()) {
    return { isValid: true, cleanZipCode: '' };
  }
  const clean = zipCode.trim();
  if (!/^\d{3,10}$/.test(clean)) {
    return { isValid: false, error: 'Zip code must be 3-10 digits' };
  }
  return { isValid: true, cleanZipCode: clean };
};

// ──────────────────────────────────────────────
// GST Number validation
// ──────────────────────────────────────────────

/**
 * Strips non-alphanumeric characters for GST input.
 * Auto-uppercased. Max 15 chars (Indian GST format: 22AAAAA0000A1Z5).
 */
export const sanitizeGstInput = (value) => {
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return cleaned.slice(0, 15);
};

/**
 * Validates an Indian GST number.
 * Format: 2-digit state code + 10-digit PAN + entity + digit + Z + alphanumeric check.
 * Optional: if empty, returns valid.
 */
export const validateGstNumber = (gst) => {
  if (!gst || !gst.trim()) {
    return { isValid: true, cleanGst: '' };
  }
  const clean = gst.trim().toUpperCase();
  // GST format: ^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$
  if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(clean)) {
    return { isValid: false, error: 'Invalid GST number format' };
  }
  return { isValid: true, cleanGst: clean };
};

// ──────────────────────────────────────────────
// Name validation
// ──────────────────────────────────────────────

/**
 * Strips digits from name input in real-time.
 */
export const sanitizeNameInput = (value) => {
  return value.replace(/\d/g, '');
};

/**
 * Validates a name field.
 * Required: must be non-empty, min 2 chars, max length, letters and spaces only,
 * and must contain at least one vowel (a,e,i,o,u,y) to reject gibberish.
 */
export const validateName = (name, fieldName = 'Name', maxLength = 50) => {
  if (!name) return { isValid: false, error: `${fieldName} is required` };
  const cleanName = name.trim();
  if (cleanName.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters` };
  }
  if (cleanName.length > maxLength) {
    return { isValid: false, error: `${fieldName} must be ${maxLength} characters or less` };
  }
  if (/\d/.test(cleanName)) {
    return { isValid: false, error: `${fieldName} cannot contain numbers` };
  }
  if (!/^[a-zA-Z\s]+$/.test(cleanName)) {
    return { isValid: false, error: `${fieldName} can only contain letters and spaces` };
  }
  // Gibberish check: name must contain at least one vowel (a, e, i, o, u, y)
  // This catches all-consonant strings like "cgdshdvchvdc" while allowing
  // valid names like "Lynn", "Bryn", "Arya" which all have at least one vowel or y.
  if (!/[aeiouyAEIOUY]/.test(cleanName)) {
    return { isValid: false, error: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
  return { isValid: true, cleanName };
};

/**
 * Validates an organization name.
 * Required: must be non-empty, max length, must contain at least one letter,
 * and the alphabetic portion must not be all-consonant gibberish (>3 letters, no vowels).
 * Short all-consonant strings (≤3 chars) are allowed for abbreviations like "HCL", "TCS".
 */
export const validateOrgName = (name, maxLength = 100) => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Please enter your organization name' };
  }
  const cleanName = name.trim();
  if (cleanName.length > maxLength) {
    return { isValid: false, error: `Organization name must be ${maxLength} characters or less` };
  }
  // Must contain at least one letter (rejects "!!!", "@#$%", etc.)
  if (!/[a-zA-Z]/.test(cleanName)) {
    return { isValid: false, error: 'Organization name must contain at least one letter' };
  }
  // Gibberish check: extract only the alphabetic characters and check for vowels.
  // Short all-consonant strings (≤3 chars) are allowed for abbreviations like HCL, TCS, 3M.
  const alphaChars = cleanName.replace(/[^a-zA-Z]/g, '');
  if (alphaChars.length > 3 && !/[aeiouyAEIOUY]/.test(alphaChars)) {
    return { isValid: false, error: 'Please enter a valid organization name' };
  }
  return { isValid: true, cleanName };
};

// ──────────────────────────────────────────────
// Email validation
// ──────────────────────────────────────────────

/**
 * Validates an email address.
 * Optional: if empty, returns valid with empty cleanEmail.
 */
export const validateEmail = (email, required = false) => {
  if (!email || !email.trim()) {
    if (required) return { isValid: false, error: 'Email is required' };
    return { isValid: true, cleanEmail: '' };
  }
  const cleanEmail = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  return { isValid: true, cleanEmail };
};

// ──────────────────────────────────────────────
// Numeric field validation (for Plans, etc.)
// ──────────────────────────────────────────────

/**
 * Validates a price/currency amount.
 * Must be a non-negative number.
 */
export const validatePrice = (value, fieldName = 'Price') => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }
  if (num < 0) {
    return { isValid: false, error: `${fieldName} cannot be negative` };
  }
  return { isValid: true, cleanValue: num };
};

/**
 * Validates a positive integer (e.g., user limits, trial days).
 * @param {number} min - Minimum value (default 0)
 */
export const validateInteger = (value, fieldName = 'Value', min = 0) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }
  if (num < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }
  return { isValid: true, cleanValue: num };
};